import * as React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { daysInStage, demandSummary } from '../lib/demand-stages.js';
import type { Demand } from '../lib/demands-api.js';
import { useT } from '../lib/i18n.js';

export interface DemandCardProps {
  readonly item: Demand;
  readonly now: Date;
  readonly onOpen: (id: string) => void;
}

/**
 * Karta poptávky (krok CRM1, bod 2). Stejné pravidlo jako `kanban-card.tsx`:
 * přesun je v DOM zakázaný, když `canUpdate=false` — makler bez práva
 * k kartu nechce zvednout, místo aby teprve dostal 403. Povolené cíle
 * přesunu NEŘEKÁ tato karta (to je `allowedTransitions` z API) — sloupec
 * si je přebírá z dat, které má karta v payloadu.
 */
export function DemandCard({ item, now, onOpen }: DemandCardProps): React.JSX.Element {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: !item.canUpdate,
    data: { stage: item.stage, allowedTransitions: item.allowedTransitions },
  });

  const style: React.CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.6 : undefined,
      }
    : undefined;

  const days = daysInStage(item.stageEnteredAt, now);
  const summary = demandSummary(item);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`rh-kanban-card${item.canUpdate ? '' : ' rh-kanban-card--locked'}`}
      data-testid={`demand-card-${item.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(item.id);
      }}
      title={item.canUpdate ? undefined : t('webAdmin.demands.noMovePermissionHint')}
    >
      <span className="rh-kanban-card__title">{item.title}</span>
      {/* Krok UXL1 bod 3: karta ukáže jméno zájemce, ne jen titulek poptávky
          (ten je o nemovitosti, ne o člověku, se kterým makléř jedná). */}
      {item.contactName !== null && item.contactName !== '' && (
        <div className="rh-kanban-card__row">
          <span data-testid={`demand-contact-${item.id}`}>{item.contactName}</span>
        </div>
      )}
      {summary !== null && (
        <div className="rh-kanban-card__row">
          <span data-testid={`demand-summary-${item.id}`}>{summary}</span>
        </div>
      )}
      <div className="rh-kanban-card__row">
        <span className="rh-kanban-card__meta">
          <span className="rh-badge rh-badge--neutral" data-testid={`demand-source-${item.id}`}>
            {t(`crm.source.${item.source}` as 'crm.source.portal')}
          </span>
          <span>
            {days === 0
              ? t('webAdmin.demands.daysInStageToday')
              : t('webAdmin.demands.daysInStage', { days: String(days) })}
          </span>
        </span>
      </div>
      {item.nextStep !== null && item.nextStep !== '' && (
        <div className="rh-kanban-card__row">
          <span data-testid={`demand-next-step-${item.id}`}>
            {t('webAdmin.demands.drawerNextStep')}: {item.nextStep}
          </span>
        </div>
      )}
    </div>
  );
}
